import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, Users, Gift, BarChart3, Shield, LogOut, 
  Clock, CheckCircle, XCircle, AlertTriangle, Eye,
  RefreshCw, Mail, ToggleLeft, ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('upload-review');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    uploadQueue: [],
    users: [],
    analytics: {},
    synthesisQueue: []
  });

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [uploadRes, usersRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/upload-queue`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/users`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/analytics`, { headers: getAuthHeaders() })
      ]);

      const [uploadQueue, users, analytics] = await Promise.all([
        uploadRes.ok ? uploadRes.json() : [],
        usersRes.ok ? usersRes.json() : [],
        analyticsRes.ok ? analyticsRes.json() : {}
      ]);

      setData({ uploadQueue, users, analytics, synthesisQueue: [] });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDocument = async (sourceId, documentType) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/upload-queue/${sourceId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ document_type: documentType })
      });
      if (response.ok) {
        toast.success('Document approved and re-processing');
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Failed to approve document');
    }
  };

  const handleRejectDocument = async (sourceId, reason) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/upload-queue/${sourceId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason })
      });
      if (response.ok) {
        toast.success('Document rejected, user notified');
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Failed to reject document');
    }
  };

  const handleToggleBeta = async (userId, enabled, expiryDate) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/beta`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled, expiry_date: expiryDate })
      });
      if (response.ok) {
        toast.success(`Beta mode ${enabled ? 'enabled' : 'disabled'}`);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Failed to update beta status');
    }
  };

  const getQueueAgeClass = (timestamp) => {
    const hours = (Date.now() - new Date(timestamp).getTime()) / (1000 * 60 * 60);
    if (hours > 72) return 'text-red-400 bg-red-500/10';
    if (hours > 48) return 'text-amber-400 bg-amber-500/10';
    return 'text-slate-400 bg-slate-500/10';
  };

  const formatTimeAgo = (timestamp) => {
    const hours = Math.floor((Date.now() - new Date(timestamp).getTime()) / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="min-h-screen bg-[#0D0F14]" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="border-b border-slate-800 bg-[#1C2130]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h1 className="font-display text-xl text-white">Strata Record Admin</h1>
                <p className="text-xs text-slate-400">{admin?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchDashboardData}
                className="text-slate-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-slate-400 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#1C2130] border border-slate-800 mb-6">
            <TabsTrigger value="upload-review" className="data-[state=active]:bg-amber-600">
              <FileText className="w-4 h-4 mr-2" />
              Upload Review
              {data.uploadQueue.length > 0 && (
                <Badge className="ml-2 bg-red-500">{data.uploadQueue.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-amber-600">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="invitations" className="data-[state=active]:bg-amber-600">
              <Gift className="w-4 h-4 mr-2" />
              Invitations
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-amber-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Section A: Upload Review Queue */}
          <TabsContent value="upload-review">
            <Card className="bg-[#1C2130] border-slate-800">
              <CardHeader>
                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  Upload Review Queue
                </CardTitle>
                <p className="text-slate-400 text-sm">
                  Documents flagged during AI parsing (confidence &lt; 0.65, unreadable, or unrecognized)
                </p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-slate-400">Loading queue...</div>
                ) : data.uploadQueue.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-slate-300">Queue is empty - all documents processed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {data.uploadQueue.map((item) => (
                      <div
                        key={item.source_id}
                        className="p-4 rounded-lg bg-[#0D0F14] border border-slate-800"
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-xs text-slate-500">
                                {item.user_id?.substring(0, 8)}...
                              </span>
                              <Badge variant="outline" className="text-slate-400">
                                {item.category || 'Unknown'}
                              </Badge>
                              <span className={`px-2 py-1 rounded text-xs ${getQueueAgeClass(item.upload_date)}`}>
                                <Clock className="w-3 h-3 inline mr-1" />
                                {formatTimeAgo(item.upload_date)}
                              </span>
                            </div>
                            <p className="text-white">{item.filename}</p>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-slate-400">
                                Flag: <span className="text-amber-400">{item.flag_reason || 'Low confidence'}</span>
                              </span>
                              {item.confidence_score !== undefined && (
                                <span className="text-slate-400">
                                  Confidence: <span className="text-red-400">{(item.confidence_score * 100).toFixed(0)}%</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-400 border-green-400/50 hover:bg-green-500/10"
                              onClick={() => handleApproveDocument(item.source_id, item.category)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-400 border-red-400/50 hover:bg-red-500/10"
                              onClick={() => handleRejectDocument(item.source_id, 'insufficient_content')}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-400 border-amber-400/50 hover:bg-amber-500/10"
                            >
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              Escalate
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Section B: User Management */}
          <TabsContent value="users">
            <Card className="bg-[#1C2130] border-slate-800">
              <CardHeader>
                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  User Management
                </CardTitle>
                <p className="text-slate-400 text-sm">
                  All registered users with account status and activity
                </p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-slate-400">Loading users...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-800 text-left">
                          <th className="pb-3 text-slate-400 text-sm font-medium">Email</th>
                          <th className="pb-3 text-slate-400 text-sm font-medium">Registered</th>
                          <th className="pb-3 text-slate-400 text-sm font-medium">Type</th>
                          <th className="pb-3 text-slate-400 text-sm font-medium">Sources</th>
                          <th className="pb-3 text-slate-400 text-sm font-medium">Synthesis</th>
                          <th className="pb-3 text-slate-400 text-sm font-medium">Last Active</th>
                          <th className="pb-3 text-slate-400 text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.users.map((user) => (
                          <tr key={user.user_id} className="border-b border-slate-800/50">
                            <td className="py-3">
                              <span className="text-white">{user.email}</span>
                              {user.founding_member && (
                                <Badge className="ml-2 bg-amber-600 text-xs">Founder</Badge>
                              )}
                            </td>
                            <td className="py-3 text-slate-400 text-sm">
                              {new Date(user.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3">
                              <Badge variant="outline" className={
                                user.beta_mode ? 'text-purple-400 border-purple-400/50' :
                                user.paid ? 'text-green-400 border-green-400/50' :
                                'text-slate-400 border-slate-400/50'
                              }>
                                {user.beta_mode ? 'Beta' : user.paid ? 'Paid' : 'Free'}
                              </Badge>
                            </td>
                            <td className="py-3 text-slate-300">{user.source_count || 0}</td>
                            <td className="py-3 text-slate-300">{user.synthesis_count || 0}</td>
                            <td className="py-3 text-slate-400 text-sm">
                              {user.last_active ? formatTimeAgo(user.last_active) : 'Never'}
                            </td>
                            <td className="py-3">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-white"
                                  onClick={() => handleToggleBeta(user.user_id, !user.beta_mode)}
                                >
                                  {user.beta_mode ? (
                                    <ToggleRight className="w-4 h-4 text-purple-400" />
                                  ) : (
                                    <ToggleLeft className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-white"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-white"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Section C: Feature Invitations */}
          <TabsContent value="invitations">
            <Card className="bg-[#1C2130] border-slate-800">
              <CardHeader>
                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                  <Gift className="w-5 h-5 text-amber-500" />
                  Special Feature Invitations
                </CardTitle>
                <p className="text-slate-400 text-sm">
                  Grant users access to gated features not yet publicly available
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { id: 'fluidity_index', name: 'Fluidity Index V2', status: 'beta' },
                    { id: 'proof_v2', name: 'Proof V2 (Enhanced Narrative)', status: 'beta' },
                    { id: 'badge_issuance', name: 'Badge Issuance', status: 'active' },
                    { id: 'role_fit', name: 'Role Fit Analysis', status: 'active' },
                    { id: 'admin_api', name: 'Admin API Access (Enterprise)', status: 'restricted' }
                  ].map((feature) => (
                    <div
                      key={feature.id}
                      className="p-4 rounded-lg bg-[#0D0F14] border border-slate-800"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-medium">{feature.name}</h4>
                        <Badge variant="outline" className={
                          feature.status === 'beta' ? 'text-purple-400 border-purple-400/50' :
                          feature.status === 'restricted' ? 'text-red-400 border-red-400/50' :
                          'text-green-400 border-green-400/50'
                        }>
                          {feature.status}
                        </Badge>
                      </div>
                      <Button size="sm" variant="outline" className="w-full mt-2">
                        Send Invitation
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Section E: Analytics Dashboard */}
          <TabsContent value="analytics">
            <Card className="bg-[#1C2130] border-slate-800">
              <CardHeader>
                <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-amber-500" />
                  Analytics Dashboard
                </CardTitle>
                <p className="text-slate-400 text-sm">
                  Product health metrics (refreshed daily)
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { 
                      label: 'Activation Rate', 
                      value: data.analytics.activation_rate || 0, 
                      suffix: '%',
                      description: 'Users with 1+ upload'
                    },
                    { 
                      label: 'Synthesis Completion', 
                      value: data.analytics.synthesis_rate || 0, 
                      suffix: '%',
                      description: 'Uploads → Synthesis'
                    },
                    { 
                      label: 'Proof Completion', 
                      value: data.analytics.proof_rate || 0, 
                      suffix: '%',
                      description: 'Synthesis → Proof'
                    },
                    { 
                      label: 'Badge Issuance', 
                      value: data.analytics.badge_rate || 0, 
                      suffix: '%',
                      description: 'Eligible → Claimed'
                    },
                    { 
                      label: 'D14 Retention', 
                      value: data.analytics.d14_retention || 0, 
                      suffix: '%',
                      description: 'Active at day 14'
                    },
                    { 
                      label: 'Avg Queue Age', 
                      value: data.analytics.avg_queue_age || 0, 
                      suffix: 'h',
                      description: 'Hours in review'
                    },
                    { 
                      label: 'Confidence Mean', 
                      value: data.analytics.confidence_mean || 0, 
                      suffix: '%',
                      description: 'Synthesis confidence'
                    },
                    { 
                      label: 'Total Users', 
                      value: data.analytics.total_users || 0, 
                      suffix: '',
                      description: 'Registered accounts'
                    }
                  ].map((metric) => (
                    <div
                      key={metric.label}
                      className="p-4 rounded-lg bg-[#0D0F14] border border-slate-800"
                    >
                      <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">
                        {metric.label}
                      </p>
                      <p className="text-3xl font-display text-white">
                        {typeof metric.value === 'number' ? metric.value.toFixed(0) : metric.value}
                        <span className="text-lg text-slate-500">{metric.suffix}</span>
                      </p>
                      <p className="text-slate-500 text-xs mt-1">{metric.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
